import React, { useState, useEffect, useContext, useMemo } from 'react';
import { getJyotish, formatJyotishDegree, getPlanetHouseJyotish, getRashiName } from './utilsJyotish';
import { LangContext } from '../../contexts/langContext';
import { StateContext } from '../../contexts/stateContext';
import { getVarnaFromChartData, getGanaFromChartData } from '../../static/jyotish_utils';
import { calculateVarnaProfile } from '../../static/jyotish_varna';
import { yoni_dictionary, nakshatra_yoni_dictionary } from '../../static/jyotish_local';
import { Svg } from '../UiKit/Svg';
import { Icon } from '../UiKit/Icon';
import { PlanetIco } from '../UiKit/Planets';
import { astroElements, astroInfo } from '../../static/astro';
import { AstroIcon } from '../UiKit/AstroIcon';
import { AstroSign } from '../UiKit/AstroSign';
import '../../components/css/astro.css';
import { JyotishChart } from './JyotishChart';
import { JyotishSummary } from './JyotishSummary';
import { VarnaRadarChart } from './VarnaRadarChart';
import { ensureAvatarCity } from '../utilsGeo';
import { Txt } from '../UiKit/Txt';




/** Цвет по элементу знака (как в AstroInfo) */
const getElementColor = (sign) => astroElements[astroInfo[sign]?.element] || 'transparent';

/** Gana → element palette (deva=water, rakshasa=fire, manu=earth) */
const GANA_ELEMENT = { Deva: 'water', Manushya: 'earth', Rakshasa: 'fire' };
const getGanaColor = (gana) => astroElements[GANA_ELEMENT[gana]] || 'transparent';

/**
 * Get Yoni information by nakshatra name
 * Handles case-insensitive lookup and returns safe fallback if not found
 * @param {string} nakshatraName - Nakshatra name (e.g., "Anuradha", "anuradha")
 * @returns {Object|null} - Yoni information object or null if not found
 */
const getYoniByNakshatra = (nakshatraName) => {
  if (!nakshatraName || typeof nakshatraName !== 'string') {
    return null;
  }

  // Normalize to lowercase for lookup
  const normalizedName = nakshatraName.toLowerCase().replace(/\s+/g, '_');

  // Look up in dictionary
  const yoniInfo = nakshatra_yoni_dictionary[normalizedName];

  if (!yoniInfo) {
    return null;
  }

  return {
    emoji: yoniInfo.yoniEmoji || '–',
    title: yoniInfo.yoniTitle || '–',
    id: yoniInfo.yoniId || null
  };
};

/**
 * Minimal Jyotish component - displays basic Vedic astrology information
 * Similar structure to Astro.js but with minimal text output
 */
export function Jyotish({ avatar, handleClick }) {
  const [kundli, setKundli] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;
  const $jyotish = context.$jyotish;
  const varnaBy = context.state?.varnaBy ?? 'nakshatra';

  useEffect(() => {
    // Reset kundli when avatar.dob or city changes
    setKundli(null);
    setError(null);

    const hasBirthData = Boolean(avatar.design?.meta?.personality || avatar.dob);
    if (hasBirthData && avatar.aggr?.city) {
      setLoading(true);
      try {
        const result = getJyotish(avatar);
        if (result && result.kundli) {
          setKundli(result.kundli);
          // console.log('result.kundli = ', JSON.stringify(result.kundli, null, 2));
          // console.log('result.kundli = ', result.kundli);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [avatar?.dob, avatar.design?.meta?.personality, avatar.aggr?.city?.location]);

  // Get Varna and Gana from chart data - must be called before conditional returns (Rules of Hooks)
  const varnaData = useMemo(() => getVarnaFromChartData(kundli, varnaBy), [kundli, varnaBy]);
  const ganaData = useMemo(() => getGanaFromChartData(kundli), [kundli]);

  if (loading) {
    return <div className="x_edu_jyotish">Loading...</div>;
  }

  if (error) {
    return <div className="x_edu_jyotish">Error: {error}</div>;
  }

  if (!kundli) {
    return <div className="x_edu_jyotish"></div>;
  }

  // Get moon's nakshatra from dasha
  const moonNakshatraName = kundli.dasha?.birthNakshatra || '–';

  // Get Yoni information for moon's nakshatra
  const moonYoni = getYoniByNakshatra(moonNakshatraName);


  const withLang = (dict, str, field) => {
    const language = context.language;
    return dict[str] ? dict[str][`${field}-${language}`] : str;
  }


  return (
    <div className="x_edu_astronumero_section" onClick={typeof handleClick === 'function' ? () => handleClick('jyotish') : void (0)}>

      <div className="x_edu_circle0">
        <div className="x_edu_circle"
          style={{
            background: `linear-gradient(to bottom, ${getElementColor(kundli.planets?.Sun?.rashiName)} 0%, ${getElementColor(kundli.planets?.Moon?.rashiName)} 50%, ${getElementColor(kundli.ascendant?.rashiName)} 100%)`,
            boxShadow: `inset 0 0 4px 1px ${getElementColor(kundli.planets?.Moon?.rashiName)}`
          }}
        >
          <AstroIcon name={kundli.planets?.Moon?.rashiName} size={24} />
        </div>
      </div>

      <div className="x_edu_astro">
        {/* Луна (Moon) */}
        {kundli.planets?.Moon && (
          <div className="f _fgap02 ">
            <PlanetIco className="x_edu_astro_icon" planet={'Moon'} /> <span><strong>{$ui(kundli.planets.Moon.rashiName) || '–'}</strong> / {moonNakshatraName}</span>
          </div>
        )}

        {/* Солнце (Sun) */}
        {kundli.planets?.Sun && (
          <div className="f _fgap02 ">
            <PlanetIco className="x_edu_astro_icon" planet={'Sun'} /> <strong>{$ui(kundli.planets.Sun.rashiName) || '–'}</strong>
          </div>
        )}

        {/* Асцендент (Ascendant) */}
        {kundli.ascendant && (
          <div className="f _fgap02 ">
            <AstroIcon name="asc" size={14} className="x_edu_astro_icon" /> <strong>{$ui(kundli.ascendant.rashiName) || '–'}</strong>
          </div>
        )}

        {/* Накшатра Луны (Moon's Nakshatra) */}
        {/* <div className="f _fgap02 ">
          <div>Накшатра: {moonNakshatraName}</div>
        </div> */}

        {/* Yoni Луны (Moon's Yoni) */}
        {moonYoni && (
          <div className="f _fgap02 ">
            {/* {$ui('jyotish_yoni_label')}:  */}
            <div className="_fm _fgap06 "><span className="_t12">{moonYoni.emoji}</span> <strong>{withLang(yoni_dictionary, moonYoni.id, 'title')}</strong></div>
          </div>
        )}

        {/* Варна (Varna) */}
        {varnaData.varna && (
          <div className="f _fgap02 ">
            {/* {$ui('jyotish_varna_label')}:  */}
            <div className="_fm _fgap06 "><span className="_t12">{$jyotish(`j_${varnaData.varna[0].toLowerCase()}`, 'emoji')}</span> <strong><Txt>{$jyotish(`j_${varnaData.varna[0].toLowerCase()}`, 'desc1')}</Txt></strong></div>
          </div>
        )}

        {/* Гана (Gana) */}
        {ganaData.gana && (
          <div className="f _fgap02 ">
            {/* {$ui('jyotish_gana_label')}:  */}
            <div className="_fm _fgap06 "><span className="_t12">{$jyotish(`j_${ganaData.gana[0].toLowerCase()}`, 'emoji')}</span> <strong><Txt>{$jyotish(`j_${ganaData.gana[0].toLowerCase()}`, 'desc1')}</Txt></strong></div>
          </div>
        )}
      </div>
    </div>
  );
}





export function JyotishMini({ avatar, handleClick }) {
  const [kundli, setKundli] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;
  const $jyotish = context.$jyotish;
  const varnaBy = context.state?.varnaBy ?? 'nakshatra';

  useEffect(() => {
    // Reset kundli when avatar.dob or city changes
    setKundli(null);
    setError(null);

    const hasBirthData = Boolean(avatar.design?.meta?.personality || avatar.dob);
    if (hasBirthData && avatar.aggr?.city) {
      setLoading(true);
      try {
        const result = getJyotish(avatar);
        if (result && result.kundli) {
          setKundli(result.kundli);
          // console.log('result.kundli = ', JSON.stringify(result.kundli, null, 2));
          // console.log('result.kundli = ', result.kundli);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [avatar?.dob, avatar.design?.meta?.personality, avatar.aggr?.city?.location]);

  // Get Varna and Gana from chart data - must be called before conditional returns (Rules of Hooks)
  const varnaData = useMemo(() => getVarnaFromChartData(kundli, varnaBy), [kundli, varnaBy]);
  const ganaData = useMemo(() => getGanaFromChartData(kundli), [kundli]);

  if (loading) {
    return <div className="x_edu_jyotish">Loading...</div>;
  }

  if (error) {
    return <div className="x_edu_jyotish">Error: {error}</div>;
  }

  if (!kundli) {
    return <div className="x_edu_jyotish"></div>;
  }

  // Get moon's nakshatra from dasha
  const moonNakshatraName = kundli.dasha?.birthNakshatra || '–';

  // Get Yoni information for moon's nakshatra
  const moonYoni = getYoniByNakshatra(moonNakshatraName);


  const withLang = (dict, str, field) => {
    const language = context.language;
    return dict[str] ? dict[str][`${field}-${language}`] : str;
  }


  return (
    <div className="_f _fc _fm _fgap0 _relative" onClick={typeof handleClick === 'function' ? () => handleClick('jyotish') : void (0)}>

      {/* <div className="x_edu_circle0">
        <div className="x_edu_circle"
          style={{
            background: `linear-gradient(to bottom, ${getElementColor(kundli.planets?.Sun?.rashiName)} 0%, ${getElementColor(kundli.planets?.Moon?.rashiName)} 50%, ${getElementColor(kundli.ascendant?.rashiName)} 100%)`,
            boxShadow: `inset 0 0 4px 1px ${getElementColor(kundli.planets?.Moon?.rashiName)}`
          }}
        >
          
        </div>
      </div> */}

      <div className="_f _fm _fc _fgap2 _fvgap1 _fwrap">
        {/* Луна (Moon) */}
        {kundli.planets?.Moon && (
          <div className="_f _fm _fgap02 ">
            <div className="_f _fgap0 _fm _o06">
              <PlanetIco className="x_edu_astro_icon" planet={'Moon'} color="#ffb700"/> 
              {$ui('planet_Moon')}: 
            </div>
            <div className="_f _fgap0 _fm yoni_page_sign">
              <AstroIcon name={kundli.planets?.Moon?.rashiName} size={16} color="#FFF" />
              <strong>{$ui(kundli.planets.Moon.rashiName) || '–'}</strong>
            </div>
          </div>
        )}

        {/* Солнце (Sun) */}
        {kundli.planets?.Sun && (
          <div className="_f _fm _fgap02 ">
            <div className="_f _fgap0 _fm _o06">
              <PlanetIco className="x_edu_astro_icon" planet={'Sun'}  color="#ffb700"/> 
              {$ui('planet_Sun')}: 
            </div>
            <div className="_f _fgap0 _fm yoni_page_sign">
              <AstroIcon name={kundli.planets?.Sun?.rashiName} size={16} color="#FFF" />
              <strong>{$ui(kundli.planets.Sun.rashiName) || '–'}</strong>
            </div>
          </div>
        )}

        {/* Асцендент (Ascendant) */}
        {kundli.ascendant && (
          <div className="_f _fm _fgap02 ">
            <div className="_f _fgap0 _fm _o06">
              <AstroIcon name="asc" size={14} className="x_edu_astro_icon" color="#ffb700" />
              {$ui('astro_ascendant')}: 
            </div>
            <div className="_f _fgap0 _fm yoni_page_sign">
              <AstroIcon name={kundli.ascendant.rashiName} size={14} className="x_edu_astro_icon" color="#FFF" />
              <strong>{$ui(kundli.ascendant.rashiName) || '–'}</strong>
             </div>
          </div>
        )}

        {/* Накшатра Луны (Moon's Nakshatra) */}
        <div className="_f _fm _fgap02 ">
          <div className="_f _fgap0 _fm _o06">
            {$ui('jyotish_nakshatra')}:
          </div>
          <strong className="yoni_page_sign">{moonNakshatraName || '–'}</strong>
        </div>


      </div>
    </div>
  );
}


export const JyotishSocial = React.memo(({ avatar, handleClick }) => {
  const [kundli, setKundli] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const hasBirthData = Boolean(avatar?.design?.meta?.personality || avatar?.dob);
      if (!cancelled) {
        setKundli(null);
        setError(null);
      }
      if (!hasBirthData) {
        if (!cancelled) setLoading(false);
        return;
      }

      if (!cancelled) setLoading(true);
      try {
        const avatarWithCity = await ensureAvatarCity(avatar);
        if (cancelled) return;
        if (!avatarWithCity?.aggr?.city?.location) {
          setLoading(false);
          return;
        }
        const result = getJyotish(avatarWithCity);
        if (result?.kundli) setKundli(result.kundli);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [avatar?.dob, avatar?.city_id, avatar?.design?.meta?.personality, avatar?.aggr?.city?.location]);

  if (loading) {
    return (
      <div className="x_social_astro" style={{ background: 'transparent' }}>
        <div className="_f _fgap02" style={{ color: '#ccdaf8' }}>
          <Icon size="lg">question_mark</Icon>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!kundli) {
    return (
      <div className="x_social_astro" aria-hidden="true">
        <div className="_f _fgap02" style={{ visibility: 'hidden' }}>
          <PlanetIco planet="Moon" />
          <strong>&nbsp;</strong>
        </div>
      </div>
    );
  }

  const moonRashi = kundli.planets?.Moon?.rashiName;
  const moonNakshatraName = kundli.dasha?.birthNakshatra || '';
  const moonYoni = getYoniByNakshatra(moonNakshatraName);
  const ganaData = getGanaFromChartData(kundli);
  const varnaProfile = calculateVarnaProfile(kundli);

  return (
    <>
      {Boolean(avatar.dob) && (
        <div
          className="x_social_astro x_social_astro--jyotish"
          style={{
            background: getGanaColor(ganaData.gana),
            boxShadow: `0 1px 0px 0px rgba(0, 0, 0, 0.13), inset 0 0 4px 0px ${getGanaColor(ganaData.gana)}`,
          }}
        >
          <div className="x_social_astro_top _fv _fgap02 _fm" style={{ textAlign: 'center' }}>
            <div className="_f _fgap02 _fm" style={{ justifyContent: 'center' }}>
              <PlanetIco planet="Moon" />
              <strong>{$ui(moonRashi) || '–'}</strong>
            </div>
            {moonNakshatraName && moonNakshatraName !== '–' && (
              <span className="x_social_astro_top_nakshatra _o08" style={{ display: 'inline', whiteSpace: 'nowrap' }}>
                {moonNakshatraName}{moonYoni ? ` ${moonYoni.emoji}` : ''}
              </span>
            )}
          </div>

          {varnaProfile?.primary && (
            <div className="varna-radar-social">
              <VarnaRadarChart distribution={varnaProfile.distribution} profile={varnaProfile} />
            </div>
          )}

        </div>
      )}
    </>
  );
});



export const JyotishSocialMini = React.memo(({ avatar, handleClick }) => {
  const [kundli, setKundli] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const hasBirthData = Boolean(avatar?.design?.meta?.personality || avatar?.dob);
      if (!cancelled) {
        setKundli(null);
        setError(null);
      }
      if (!hasBirthData) {
        if (!cancelled) setLoading(false);
        return;
      }

      if (!cancelled) setLoading(true);
      try {
        const avatarWithCity = await ensureAvatarCity(avatar);
        if (cancelled) return;
        if (!avatarWithCity?.aggr?.city?.location) {
          setLoading(false);
          return;
        }
        const result = getJyotish(avatarWithCity);
        if (result?.kundli) setKundli(result.kundli);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [avatar?.dob, avatar?.city_id, avatar?.design?.meta?.personality, avatar?.aggr?.city?.location]);

  if (loading) {
    return (
      <div className="x_social_astro" style={{ background: 'transparent' }}>
        <div className="_f _fgap02" style={{ color: '#ccdaf8' }}>
          <Icon size="lg">question_mark</Icon>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!kundli) {
    return (
      <div className="x_social_astro" aria-hidden="true">
        <div className="_f _fgap02" style={{ visibility: 'hidden' }}>
          <PlanetIco planet="Moon" />
          <strong>&nbsp;</strong>
        </div>
      </div>
    );
  }

  const moonRashi = kundli.planets?.Moon?.rashiName;
  const moonNakshatraName = kundli.dasha?.birthNakshatra || '';
  const moonYoni = getYoniByNakshatra(moonNakshatraName);
  const ganaData = getGanaFromChartData(kundli);
  const varnaProfile = calculateVarnaProfile(kundli);

  return (
    <>
      {Boolean(avatar.dob) && (
        <div
          className="x_social_astro x_social_astro--jyotish"
          style={{
            background: getGanaColor(ganaData.gana),
            boxShadow: `0 1px 0px 0px rgba(0, 0, 0, 0.13), inset 0 0 4px 0px ${getGanaColor(ganaData.gana)}`,
          }}
        >
          <div className="x_social_astro_top _fv _fgap02 _fm" style={{ textAlign: 'center' }}>
            <div className="_f _fgap02 _fm" style={{ justifyContent: 'center' }}>
              <PlanetIco planet="Moon" />
              <strong>{$ui(moonRashi) || '–'}</strong>
            </div>
            {moonNakshatraName && moonNakshatraName !== '–' && (
              <span className="x_social_astro_top_nakshatra _o08" style={{ display: 'inline', whiteSpace: 'nowrap' }}>
                {moonNakshatraName}{moonYoni ? ` ${moonYoni.emoji}` : ''}
              </span>
            )}
          </div>

          {varnaProfile?.primary && (
            <div className="varna-radar-social">
              <VarnaRadarChart distribution={varnaProfile.distribution} profile={varnaProfile} />
            </div>
          )}

        </div>
      )}
    </>
  );
});


export function JyotishInfo({ avatar }) {
  const [kundli, setKundli] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [houseSystem, setHouseSystem] = useState('whole_sign');

  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;

  const getPlanetKey = (planetKey) => {
    if (planetKey === 'neutral') return '';
    if (planetKey === 'exalted') return '▲';
    if (planetKey === 'debilitated') return '▼';
    return planetKey;
  };

  /** Key for $ui: Rahu→North_Node, Ketu→South_Node (как в AstroInfo) */
  const planetUiKey = (key) => key;

  useEffect(() => {
    setKundli(null);
    setError(null);
    const hasBirthData = Boolean(avatar.design?.meta?.personality || avatar.dob);
    if (hasBirthData && avatar.aggr?.city) {
      setLoading(true);
      try {
        const result = getJyotish(avatar, { houseSystem });
        if (result?.kundli) {
          setKundli(result.kundli);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [avatar?.dob, avatar.design?.meta?.personality, avatar.aggr?.city?.location, houseSystem]);

  if (loading) {
    return <div className="astro-chart">Loading Jyotish…</div>;
  }
  if (error) {
    return <div className="astro-chart">Error: {error}</div>;
  }
  if (!kundli) {
    return <div className="astro-chart"></div>;
  }

  const planetOrder = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
  const planetsList = planetOrder
    .map((key) => ({ key, ...kundli.planets?.[key] }))
    .filter((p) => p.longitude != null);

  return (
    <div className="astro-chart">
      <div className="_fv _fgap1">

      <div className="x_info_w _fv _fgap0">
      {kundli.birthDetails && (
            <div className="_fv _fgap0 _f100 mobile_v_no" style={{ padding: '1.2em 0 0 0' }}>
              <h2>{$ui('jyotish_title')}</h2>
              {/* <div className="">
                <div>Date: {kundli.birthDetails.date ? new Date(kundli.birthDetails.date).toLocaleString() : '–'}</div>
                <div>Lat: {kundli.birthDetails.lat ?? '–'}, Lon: {kundli.birthDetails.lon ?? '–'}</div>
                <div>Timezone: {kundli.birthDetails.timezone ?? '–'}</div>
              </div> */}
            </div>
          )}

          <div className="x_beta_notice _t08">{$ui('jyotish_beta')}</div>

        </div>

        <div className="chart-visualization _fv _fcc" style={{ width: '100%'}}>
          <JyotishChart
            kundli={kundli}
            houseSystem={houseSystem}
            onHouseSystemChange={setHouseSystem}
            labels={{
              north: $ui('jyotish_chart_north'),
              south: $ui('jyotish_chart_south'),
              wholeSign: $ui('jyotish_house_whole_sign'),
              equalHouse: $ui('jyotish_house_equal_house'),
            }}
          />
        </div>

        <div className="x_info_w _fv">
          {/* Birth details */}


          <JyotishSummary kundli={kundli} />

          {/* Planets table */}
          <div className="_fv _fgap0 _f100" style={{ marginTop: '1em' }}>
            <h4>{$ui('astro_planets')}</h4>
            <div className="astro-table">
              <div className="astro-table-header astro-table-header-jyotish">
                <div className="astro-table-cell">{$ui('astro_planet')}</div>
                <div className="astro-table-cell">{$ui('astro_sign')}</div>
                <div className="astro-table-cell">{$ui('astro_degree')}</div>
                <div className="astro-table-cell">{$ui('astro_house')}</div>
              </div>
              {planetsList.map((planet, idx) => (
                <div key={planet.key || idx} className="astro-table-row astro-table-row-jyotish">
                  <div className="astro-table-cell">
                    <div className="_f _fgap04 _fm">
                      <PlanetIco planet={planet.key === 'Rahu' ? 'North_Node' : planet.key === 'Ketu' ? 'South_Node' : planet.key} />
                      <b>{$ui(`planet_${planetUiKey(planet.key)}`)}</b> {getPlanetKey(planet.dignity)}
                    </div>
                  </div>
                  <div
                    className="astro-table-cell"
                    style={{ backgroundColor: getElementColor(planet.rashiName) }}
                  >
                    <div className="_f _fgap04 _fm">
                      <AstroSign size={12} sign={planet.rashiName} /> {$ui(planet.rashiName) || '–'}
                    </div>
                  </div>
                  <div className="astro-table-cell">{formatJyotishDegree(planet.degree)} {planet.isRetrograde ? ' R' : ''}</div>
                  <div className="astro-table-cell">{getPlanetHouseJyotish(planet, kundli.houses)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Houses */}
          {kundli.houses?.length > 0 && (
            <div className="_fv _fgap0 _f100" style={{ marginTop: '1em' }}>
              <h4>{$ui('jyotish_bhavas_label')}</h4>
              <div className="astro-table">
                <div className="astro-table-header astro-table-header-jyotish-bhavas">
                  <div className="astro-table-cell">{$ui('jyotish_bhava_label')}</div>
                  <div className="astro-table-cell">{$ui('astro_sign')}</div>
                  <div className="astro-table-cell">{$ui('astro_planets')}</div>
                </div>
                {kundli.houses.filter((h) => h.planets?.length).map((h, i) => {
                  const rashiName = getRashiName(h.rashi);
                  return (
                    <div key={h.number ?? i} className="astro-table-row astro-table-row-jyotish-bhavas">
                      <div className="astro-table-cell">
                        <b>{h.number}</b>
                      </div>
                      <div
                        className="astro-table-cell"
                        style={{ backgroundColor: getElementColor(rashiName) }}
                      >
                        <div className="_f _fgap04 _fm">
                          <AstroSign size={12} sign={rashiName} /> {$ui(rashiName) || '–'}
                        </div>
                      </div>
                      <div className="astro-table-cell">
                        {h.planets.map((pk) => (
                          <span key={pk} className="_f _fgap04 _fm">
                            <PlanetIco planet={pk === 'Rahu' ? 'North_Node' : pk === 'Ketu' ? 'South_Node' : pk} />
                            {$ui(`planet_${planetUiKey(pk)}`)}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dasha */}
          {kundli.dasha && (
            <div className="_fv _fgap0 _f100" style={{ marginTop: '1em' }}>
              <h4>Dasha</h4>
              <div className="astro-table _fv _fgap06">
                <div><strong>Birth Nakshatra:</strong> {kundli.dasha.birthNakshatra ?? '–'} (pada {kundli.dasha.nakshatraPada ?? '–'})</div>
                <div><strong>Balance:</strong> {kundli.dasha.dashaBalance ?? '–'}</div>
                {kundli.dasha.currentMahadasha && (
                  <div><strong>Mahadasha:</strong> {kundli.dasha.currentMahadasha.planet} until {kundli.dasha.currentMahadasha.endTime ? new Date(kundli.dasha.currentMahadasha.endTime).toLocaleDateString() : '–'}</div>
                )}
                {kundli.dasha.currentAntardasha && (
                  <div><strong>Antardasha:</strong> {kundli.dasha.currentAntardasha.planet} until {kundli.dasha.currentAntardasha.endTime ? new Date(kundli.dasha.currentAntardasha.endTime).toLocaleDateString() : '–'}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 